import en from './messages/en.json'
import { APP_NAME } from '../config/app'
import { DEFAULT_LOCALE, type Locale } from './locales'
import { resolveParticleMarks } from '../lib/josa'

/**
 * 화면 문구.
 *
 * 영어만 첫 화면 청크에 넣는다. 기본 언어라서 기다림 없이 바로 그릴 수 있어야 한다.
 * 나머지 언어는 선택됐을 때 `loadMessages()` 로 받아 레지스트리에 넣는다.
 * `t()` 는 동기 함수라, 화면을 그리기 전에 그 언어가 로드돼 있어야 한다 (main.tsx 와 언어 메뉴가 보장).
 */

export type Messages = typeof en
export type { StringKey } from './keys'
import type { StringKey } from './keys'

const registry = new Map<Locale, Messages>([['en', en]])

/** 영어를 뺀 언어 파일. Vite 가 언어별로 청크를 나눈다. */
const loaders = import.meta.glob<{ default: Messages }>(['./messages/*.json', '!./messages/en.json'])

export async function loadMessages(locale: Locale): Promise<void> {
  if (registry.has(locale)) return
  const loader = loaders[`./messages/${locale}.json`]
  if (!loader) throw new Error(`언어 파일이 없다: ${locale}`)
  const mod = await loader()
  registry.set(locale, mod.default)
}

export type Vars = Record<string, string | number>

/**
 * 문구를 꺼내 자리표시자를 채운다. `{appName}` 은 항상 채워진다.
 * 로드되지 않은 언어이거나 키가 비어 있으면 영어로 대신한다 (키 누락은 테스트가 0건으로 막는다).
 */
export function t(locale: Locale, key: StringKey, vars: Vars = {}): string {
  const table = registry.get(locale) ?? registry.get(DEFAULT_LOCALE)!
  const raw = table[key] || en[key] || key
  const localized = locale === 'ja' ? raw.replaceAll('·', '・') : raw
  const out = interpolate(locale === 'pt-BR' || locale === 'es' ? contractAtSlots(localized, vars, locale) : localized, { appName: APP_NAME, ...vars })
  // 한국어 조사 표시 "(이)가" 등을 받침에 맞게
  if (locale === 'ko') return resolveParticleMarks(out)
  // 스페인어·포르투갈어: 문장 안 기운 이름 뒤 한자
  if (locale === 'es' || locale === 'pt-BR') return dropHanjaInSentence(out)
  return out
}

/**
 * 카드 장수 — 1장이면 단수 (en "1 card", es·pt "1 carta"). 예전에는 "1 cards / 1 cartas".
 * 단수 문구는 이미 있던 `cardOne`(질문 목록의 "1장") 을 그대로 쓴다 — 같은 값의 키를 하나 더 두지 않는다.
 */
export function cardsCount(locale: Locale, n: number, many: 'cardsN' | 'coinCards' = 'cardsN'): string {
  return n === 1 ? t(locale, 'cardOne') : t(locale, many, { n })
}

/**
 * **문장 안에서는 기운 이름 뒤 한자를 뗀다** (es·pt). 기운 이름 문구가 "Madera 木" 이라 그대로 끼우면
 * "tu carta corta en Madera 木 y…" 처럼 문장 한가운데 한자가 섰다. 이름표 하나만 있는 자리("Madera 木")는 그대로 둔다.
 */
const ELEMENT_WITH_HANJA = /(Madera|Fuego|Tierra|Metal|Agua|Madeira|Fogo|Terra|Água) [木火土金水](?![\p{L}])/gu
export function dropHanjaInSentence(text: string): string {
  if (/^\s*\S+ [木火土金水]\s*$/u.test(text)) return text
  return text.replace(ELEMENT_WITH_HANJA, '$1')
}

/**
 * **자리 앞 전치사 + 넣은 값의 관사를 한 낱말로** (pt "em a Madeira" → "na Madeira", "de o mês" → "do mês",
 * "por a" → "pela"; es "de el" → "del", "a el" → "al"). 문구표의 "em {x}" 에 관사로 시작하는 값이 들어올 때만 고친다 —
 * 미리 쓴 글의 "antes de o dia acabar"(부정사 주어, 바른 말)는 건드리지 않는다.
 */
const PT_JOIN: Record<string, Record<string, string>> = {
  em: { o: 'no', a: 'na', os: 'nos', as: 'nas' },
  de: { o: 'do', a: 'da', os: 'dos', as: 'das' },
  por: { o: 'pelo', a: 'pela', os: 'pelos', as: 'pelas' },
  a: { o: 'ao', a: 'à', os: 'aos', as: 'às' },
}
const ES_JOIN: Record<string, Record<string, string>> = { de: { el: 'del' }, a: { el: 'al' } }

export function contractAtSlots(template: string, vars: Vars, locale: Locale): string {
  const table = locale === 'pt-BR' ? PT_JOIN : ES_JOIN
  return template.replace(/(^|[^\p{L}])(em|Em|de|De|por|Por|a|A)( )\{(\w+)\}/gu, (whole, pre: string, prep: string, sp: string, name: string) => {
    const value = vars[name]
    if (value === undefined) return whole
    const m = /^(o|a|os|as|el|O|A|Os|As|El)( )/u.exec(String(value))
    if (!m) return whole
    const joined = table[prep.toLowerCase()]?.[m[1]!.toLowerCase()]
    if (!joined) return whole
    const cased = prep[0] === prep[0]!.toUpperCase() ? joined.charAt(0).toUpperCase() + joined.slice(1) : joined
    // 자리 하나를 "{name}" 대신 "합친 말 + 관사 뗀 값" 으로 미리 채운다
    return `${pre}${cased}${sp}${String(value).slice(m[0].length)}`
  })
}

export function interpolate(template: string, vars: Vars): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  )
}

